<template>
  <div class="tool-call-pill" :class="containerClass">
    <v-icon :class="iconClass" size="14">{{ icon }}</v-icon>
    <span class="pill-label">{{ label }}</span>
    <span v-if="summary" class="pill-summary">· {{ summary }}</span>
    <span v-if="durationLabel" class="pill-duration">· {{ durationLabel }}</span>
  </div>
</template>

<script>
import { computed, defineComponent } from 'vue';

const TOOL_LABELS = {
  list_job_hierarchies: 'Reading hierarchies',
  search_jobs_by_name: 'Searching jobs',
  get_job_details: 'Reading job details',
  get_job_by_reference: 'Looking up reference',
};

export default defineComponent({
  name: 'ToolCallIndicator',
  props: {
    call: { type: Object, required: true },
  },
  setup(props) {
    const label = computed(() => TOOL_LABELS[props.call.name] || props.call.name);

    const summary = computed(() => {
      const input = props.call.input || {};
      if (props.call.name === 'search_jobs_by_name' && typeof input.query === 'string') {
        return `"${input.query}"`;
      }
      if (props.call.name === 'get_job_details' && Array.isArray(input.node_ids)) {
        return `${input.node_ids.length} id${input.node_ids.length === 1 ? '' : 's'}`;
      }
      if (props.call.name === 'get_job_by_reference' && Array.isArray(input.references)) {
        return input.references.length === 1
          ? input.references[0]
          : `${input.references.length} refs`;
      }
      return '';
    });

    const durationLabel = computed(() => {
      if (props.call.status === 'running') return null;
      if (typeof props.call.duration_ms !== 'number') return null;
      if (props.call.duration_ms < 1000) return `${props.call.duration_ms}ms`;
      return `${(props.call.duration_ms / 1000).toFixed(1)}s`;
    });

    const icon = computed(() => {
      switch (props.call.status) {
        case 'running': return 'mdi-progress-clock';
        case 'ok': return 'mdi-check-circle';
        case 'error': return 'mdi-alert-circle';
        default: return 'mdi-information';
      }
    });

    const containerClass = computed(() => `tool-call-pill--${props.call.status}`);
    const iconClass = computed(() => (props.call.status === 'running' ? 'spin-icon' : ''));

    return { label, summary, durationLabel, icon, containerClass, iconClass };
  },
});
</script>

<style scoped>
.tool-call-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 10px;
  border-radius: 9999px;
  font-size: 11px;
  border: 1px solid;
  white-space: nowrap;
}
.tool-call-pill--running {
  background: rgb(239 246 255);
  border-color: rgb(191 219 254);
  color: rgb(29 78 216);
}
.tool-call-pill--ok {
  background: rgb(249 250 251);
  border-color: rgb(229 231 235);
  color: rgb(55 65 81);
}
.tool-call-pill--error {
  background: rgb(254 242 242);
  border-color: rgb(254 202 202);
  color: rgb(185 28 28);
}
.pill-label { font-weight: 500; }
.pill-summary { color: rgb(107 114 128); }
.pill-duration { color: rgb(156 163 175); }
.spin-icon {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
